// Zerodha CSV Parser
// CSV Structure: symbol,isin,trade_date,exchange,segment,series,trade_type,auction,quantity,price,trade_id,order_id,order_execution_time

export interface ZerodhaTrade {
  symbol: string;
  isin: string;
  trade_date: string;
  exchange: string;
  segment: string;
  series: string;
  trade_type: 'buy' | 'sell';
  auction: boolean;
  quantity: number;
  price: number;
  trade_id: string;
  order_id: string;
  order_execution_time: string;
}

export interface ParsedTranche {
  orderId: string;
  symbol: string;
  isin: string;           // ISIN for canonical identification
  type: 'buy' | 'sell';
  quantity: number;
  avgPrice: number;
  timestamp: string;
  exchange: string;
}

export interface ParsedPosition {
  symbol: string;
  isin: string;           // ISIN for canonical identification
  exchange: string;       // Exchange (NSE/BSE)
  tranches: ParsedTranche[];
}

/**
 * Result of CSV parsing including rejected rows
 */
export interface ParseResult {
  positions: ParsedPosition[];
  rejectedRows: RejectedRow[];
}

export interface RejectedRow {
  rowNumber: number;
  symbol?: string;
  reason: string;
  rawData?: string;
}

/**
 * Parse Zerodha tradebook CSV file
 * Returns positions grouped by ISIN (not symbol)
 */
export async function parseZerodhaCSV(file: File): Promise<ParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const { trades, rejectedRows } = parseCSVText(text);
        const positions = groupTradesIntoPositions(trades);
        resolve({ positions, rejectedRows });
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
}

/**
 * Parse CSV text into trade objects
 * Rejects rows without ISIN
 */
function parseCSVText(text: string): { trades: ZerodhaTrade[]; rejectedRows: RejectedRow[] } {
  // Normalize line endings (handle Windows CRLF, Mac CR, Unix LF)
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalizedText.trim().split('\n');
  
  console.log('[ZerodhaParser] Total lines:', lines.length);
  
  if (lines.length < 2) {
    throw new Error('CSV file is empty or invalid');
  }
  
  // Parse header to find column indices (more robust)
  const header = lines[0].toLowerCase().split(',').map(h => h.trim());
  console.log('[ZerodhaParser] Header:', header);
  
  const colIndex = {
    symbol: header.indexOf('symbol'),
    isin: header.indexOf('isin'),
    trade_date: header.indexOf('trade_date'),
    exchange: header.indexOf('exchange'),
    segment: header.indexOf('segment'),
    series: header.indexOf('series'),
    trade_type: header.indexOf('trade_type'),
    auction: header.indexOf('auction'),
    quantity: header.indexOf('quantity'),
    price: header.indexOf('price'),
    trade_id: header.indexOf('trade_id'),
    order_id: header.indexOf('order_id'),
    order_execution_time: header.indexOf('order_execution_time'),
  };
  
  // Verify required columns exist
  if (colIndex.isin === -1) {
    throw new Error('CSV file is missing required ISIN column');
  }
  if (colIndex.symbol === -1) {
    throw new Error('CSV file is missing required symbol column');
  }
  
  console.log('[ZerodhaParser] Column indices:', colIndex);
  
  // Skip header row
  const dataLines = lines.slice(1);
  console.log('[ZerodhaParser] Data lines:', dataLines.length);
  
  const trades: ZerodhaTrade[] = [];
  const rejectedRows: RejectedRow[] = [];
  
  for (let i = 0; i < dataLines.length; i++) {
    const line = dataLines[i];
    if (!line.trim()) continue;
    
    const rowNumber = i + 2; // +2 because we skipped header and arrays are 0-indexed
    const values = parseCSVLine(line);
    
    if (values.length < 13) {
      rejectedRows.push({
        rowNumber,
        reason: `Invalid row format (${values.length} columns, expected 13)`,
        rawData: line.substring(0, 100),
      });
      continue;
    }
    
    const symbol = values[colIndex.symbol]?.trim();
    const isin = values[colIndex.isin]?.trim();
    
    // CRITICAL: Reject rows without ISIN
    if (!isin || isin === '') {
      rejectedRows.push({
        rowNumber,
        symbol,
        reason: 'Missing ISIN - cannot identify security',
        rawData: line.substring(0, 100),
      });
      continue;
    }
    
    // Validate ISIN format (should start with IN for Indian securities)
    if (!isin.startsWith('IN') || isin.length !== 12) {
      rejectedRows.push({
        rowNumber,
        symbol,
        reason: `Invalid ISIN format: ${isin}`,
        rawData: line.substring(0, 100),
      });
      continue;
    }
    
    const trade: ZerodhaTrade = {
      symbol: symbol,
      isin: isin,
      trade_date: values[colIndex.trade_date] || '',
      exchange: values[colIndex.exchange] || 'NSE',
      segment: values[colIndex.segment] || '',
      series: values[colIndex.series] || '',
      trade_type: (values[colIndex.trade_type]?.toLowerCase() || 'buy') as 'buy' | 'sell',
      auction: values[colIndex.auction] === 'true',
      quantity: parseFloat(values[colIndex.quantity] || '0'),
      price: parseFloat(values[colIndex.price] || '0'),
      trade_id: values[colIndex.trade_id] || '',
      order_id: values[colIndex.order_id] || '',
      order_execution_time: values[colIndex.order_execution_time] || '',
    };
    
    trades.push(trade);
  }
  
  console.log('[ZerodhaParser] Parsed trades:', trades.length);
  console.log('[ZerodhaParser] Rejected rows:', rejectedRows.length);
  
  return { trades, rejectedRows };
}

/**
 * Parse a single CSV line handling quoted values
 */
function parseCSVLine(line: string): string[] {
  const values: string[] = [];
  let currentValue = '';
  let insideQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      insideQuotes = !insideQuotes;
    } else if (char === ',' && !insideQuotes) {
      values.push(currentValue.trim());
      currentValue = '';
    } else {
      currentValue += char;
    }
  }
  
  // Push the last value
  values.push(currentValue.trim());
  
  return values;
}

/**
 * Group trades by order_id into tranches, then by ISIN into positions
 * IMPORTANT: Groups by ISIN, not symbol (as same security can have different symbols)
 */
function groupTradesIntoPositions(trades: ZerodhaTrade[]): ParsedPosition[] {
  // Step 1: Group trades by order_id to create tranches
  const orderMap = new Map<string, ZerodhaTrade[]>();
  
  for (const trade of trades) {
    if (!orderMap.has(trade.order_id)) {
      orderMap.set(trade.order_id, []);
    }
    orderMap.get(trade.order_id)!.push(trade);
  }
  
  // Step 2: Convert order groups to tranches
  const tranches: ParsedTranche[] = [];
  
  for (const [orderId, orderTrades] of orderMap.entries()) {
    // All trades in an order should have same symbol, isin, and type
    const firstTrade = orderTrades[0];
    
    // Calculate total quantity and weighted average price
    let totalQuantity = 0;
    let totalValue = 0;
    
    for (const trade of orderTrades) {
      totalQuantity += trade.quantity;
      totalValue += trade.quantity * trade.price;
    }
    
    const avgPrice = totalValue / totalQuantity;
    
    tranches.push({
      orderId,
      symbol: firstTrade.symbol,
      isin: firstTrade.isin,
      type: firstTrade.trade_type,
      quantity: totalQuantity,
      avgPrice: Math.round(avgPrice * 100) / 100, // Round to 2 decimals
      timestamp: firstTrade.order_execution_time,
      exchange: firstTrade.exchange,
    });
  }
  
  // Step 3: Group tranches by ISIN to create positions (not by symbol!)
  const isinMap = new Map<string, ParsedTranche[]>();
  
  for (const tranche of tranches) {
    if (!isinMap.has(tranche.isin)) {
      isinMap.set(tranche.isin, []);
    }
    isinMap.get(tranche.isin)!.push(tranche);
  }
  
  // Step 4: Sort tranches by timestamp for each position
  const positions: ParsedPosition[] = [];
  
  for (const [isin, isinTranches] of isinMap.entries()) {
    // Sort by timestamp
    isinTranches.sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    
    // Get symbol and exchange from the first tranche
    const firstTranche = isinTranches[0];
    
    positions.push({
      symbol: firstTranche.symbol,
      isin,
      exchange: firstTranche.exchange,
      tranches: isinTranches,
    });
  }
  
  // Sort positions by symbol name for display
  positions.sort((a, b) => a.symbol.localeCompare(b.symbol));
  
  return positions;
}

/**
 * Validate if file is a valid CSV
 */
export function isValidCSV(file: File): boolean {
  return file.type === 'text/csv' || 
         file.name.toLowerCase().endsWith('.csv');
}
